import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { FinancialEntry } from '../../shared/types/financial';
import { Card, Button } from '../ui';

interface PropertyFinancialsProps {
  portfolioId: string;
  propertyId: string;
  entries: FinancialEntry[];
  loading: boolean;
  onAddEntry: () => void;
  onEditEntry: (entry: FinancialEntry) => void;
  onDeleteEntry: (entry: FinancialEntry) => void;
}

export function PropertyFinancials({ 
  portfolioId,
  propertyId,
  entries, 
  loading, 
  onAddEntry, 
  onEditEntry, 
  onDeleteEntry 
}: PropertyFinancialsProps) {
  const styles = useStyles();
  const router = useRouter();
  const { isMobile, isSmallMobile } = useResponsive();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Card style={{ 
      marginBottom: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.xl,
      paddingBottom: isMobile ? styles.utils.spacing.xl : undefined,
    }}>
      <View style={{ marginBottom: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.lg }}>
        <Text style={[styles.sectionTitle, { 
          marginBottom: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.md,
          fontSize: isSmallMobile ? styles.utils.typography.sizes.base : undefined,
        }]}>
          Financial Transactions
        </Text>
        <View style={[styles.row, { 
          gap: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm, 
          flexWrap: 'wrap',
        }]}>
          <Button
            title={isSmallMobile ? "Mortgage" : "Mortgage Calculator"}
            onPress={() => router.push(`/portfolios/${portfolioId}/properties/${propertyId}/mortgage`)}
            variant="secondary"
            leftIcon="calculator"
            size="sm"
            style={{ flex: isMobile ? 1 : undefined, minWidth: 0 }}
          />
          <Button
            title={isSmallMobile ? "+ Entry" : "+ Add Entry"}
            onPress={onAddEntry}
            variant="primary"
            size="sm"
            style={{ flex: isMobile ? 1 : undefined }}
          />
        </View>
      </View>

      {loading ? (
        <View style={[styles.emptyState, { padding: styles.utils.spacing.xl }]}>
          <ActivityIndicator size="large" color={styles.tokens.color.primary} />
          <Text style={styles.loadingText}>Loading financial entries...</Text>
        </View>
      ) : entries.length === 0 ? (
        <View style={{
          padding: styles.utils.spacing.xl,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Text style={[styles.text('base'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.6),
            textAlign: 'center',
            marginBottom: styles.utils.spacing.md,
          }]}>
            No financial entries yet.
          </Text>
          <Text style={[styles.text('sm'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.5),
            textAlign: 'center',
          }]}>
            Click "+ Add Entry" to get started!
          </Text>
        </View>
      ) : (
        <View style={{ gap: styles.utils.spacing.md }}>
          {entries.map((entry) => (
            <Card key={entry.id} elevated style={{ 
              marginBottom: 0,
              padding: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
            }}>
              <View style={[styles.row, { 
                justifyContent: 'space-between', 
                marginBottom: styles.utils.spacing.sm,
                flexWrap: 'wrap',
                gap: styles.utils.spacing.sm,
                minWidth: 0,
              }]}>
                <View style={[styles.row, { 
                  alignItems: 'center', 
                  flex: 1, 
                  gap: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
                  minWidth: 0,
                  flexShrink: 1,
                }]}>
                  <View style={{
                    paddingHorizontal: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
                    paddingVertical: styles.utils.spacing.xs,
                    borderRadius: styles.utils.radius.md,
                    backgroundColor: entry.type === 'income' 
                      ? addColorOpacity(styles.tokens.color.success, 0.2) 
                      : addColorOpacity(styles.tokens.color.error, 0.2),
                    flexShrink: 0,
                  }}>
                    <Text style={[styles.text('xs'), { 
                      color: entry.type === 'income' ? styles.tokens.color.success : styles.tokens.color.error,
                      fontWeight: styles.utils.typography.weights.bold,
                    }]}>
                      {entry.type.toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text 
                      style={[styles.heading(isSmallMobile ? 'sm' : 'base'), { 
                        fontWeight: styles.utils.typography.weights.bold,
                        color: entry.type === 'income' ? styles.tokens.color.success : styles.tokens.color.error,
                      }]}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {entry.type === 'income' ? '+' : '-'}${entry.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    <Text style={[styles.text('xs'), { 
                      color: addColorOpacity(styles.tokens.color.onBackground, 0.6),
                      marginTop: styles.utils.spacing.xs,
                    }]}>
                      {formatDate(entry.date)}
                    </Text>
                  </View>
                </View>
                <View style={[styles.row, { 
                  gap: styles.utils.spacing.sm,
                  flexShrink: 0,
                  flexWrap: 'wrap',
                }]}>
                  <Button
                    title="Edit"
                    onPress={() => onEditEntry(entry)}
                    variant="outline"
                    size="sm"
                  />
                  <Button
                    title="Delete"
                    onPress={() => onDeleteEntry(entry)}
                    variant="danger"
                    size="sm"
                  />
                </View>
              </View>
              
              {entry.description && (
                <Text style={[styles.text(isSmallMobile ? 'sm' : 'base'), { 
                  marginBottom: styles.utils.spacing.xs,
                  marginTop: styles.utils.spacing.xs,
                }]}>
                  {entry.description}
                </Text>
              )}
              
              {entry.category && (
                <View style={[styles.row, { 
                  alignItems: 'center',
                  gap: styles.utils.spacing.xs,
                  marginTop: styles.utils.spacing.xs,
                }]}>
                  <Text style={[styles.text('xs'), { 
                    color: addColorOpacity(styles.tokens.color.onBackground, 0.6),
                  }]}>
                    Category:
                  </Text>
                  <Text style={[styles.text('sm'), { 
                    color: styles.tokens.color.onBackground,
                    fontWeight: styles.utils.typography.weights.medium,
                  }]}>
                    {entry.category}
                  </Text>
                </View>
              )}
              
              {entry.is_recurring && (
                <View style={{
                  marginTop: styles.utils.spacing.sm,
                  paddingHorizontal: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
                  paddingVertical: styles.utils.spacing.xs,
                  backgroundColor: addColorOpacity(styles.tokens.color.secondary, 0.15),
                  borderRadius: styles.utils.radius.md,
                  alignSelf: 'flex-start',
                  borderWidth: 1,
                  borderColor: addColorOpacity(styles.tokens.color.secondary, 0.3),
                }}>
                  <Text style={[styles.text('xs'), { 
                    color: styles.tokens.color.secondary,
                    fontWeight: styles.utils.typography.weights.medium,
                  }]}>
                    🔄 Recurring: {entry.recurrence_frequency}
                    {entry.recurrence_end_date && ` until ${new Date(entry.recurrence_end_date).toLocaleDateString()}`}
                  </Text>
                </View>
              )}
            </Card>
          ))}
        </View>
      )}
    </Card>
  );
}

